import { setActiveMunicipalityId } from "@/helpers/activeMunicipality";
import { apiClient } from "@/helpers/apiClient";
import queryClient from "@/helpers/queryClient";
import type { RouteTypes } from "@energovizor/api-client";
import {
  createSlice,
  createAsyncThunk,
  type SerializedError,
} from "@reduxjs/toolkit";
import type { AxiosError } from "axios";

type UserAccountType = "client" | "admin";
type UserPayload = RouteTypes['/auth/me']['get']['response']['payload'];
type UserData = UserPayload & { type?: UserAccountType };

const normalizeUserPayload = (
  payload: UserPayload | null | undefined,
  fallbackType?: UserAccountType
): UserData | null => {
  if (!payload) return null;

  const normalizedType =
    (payload as { type?: UserAccountType })?.type ??
    payload.accountType ??
    fallbackType;

  return {
    ...payload,
    ...(normalizedType ? { type: normalizedType } : {}),
  };
};

const REGISTER_USER = "user/register";
export const registerUser = createAsyncThunk(
  REGISTER_USER,
  async (userData: RouteTypes['/auth/register']['post']['body'], thunkAPI) => {
    setActiveMunicipalityId(null);
    try {
      const res = await apiClient.post('/auth/register', userData);
      return normalizeUserPayload(res.data.payload as UserPayload);
    } catch (error) {
      return thunkAPI.rejectWithValue((error as AxiosError).response);
    }
  }
);

const LOGIN_USER = "user/login";
export const loginUser = createAsyncThunk(
  LOGIN_USER,
  async (credentials: RouteTypes['/auth/login']['post']['body'], thunkAPI) => {
    setActiveMunicipalityId(null);
    try {
      const res = await apiClient.post('/auth/login', credentials);
      return normalizeUserPayload(res.data.payload as UserPayload);
    } catch (error) {
      return thunkAPI.rejectWithValue((error as AxiosError).response);
    }
  }
);

const LOGOUT_USER = "user/logout";
export const logoutUser = createAsyncThunk(LOGOUT_USER, async () => {
  try {
    await apiClient.delete('/auth/session');
  } catch {
    // Session may already be invalidated on backend, keep local logout flow stable.
  } finally {
    setActiveMunicipalityId(null);
    await queryClient.cancelQueries();
    queryClient.clear();
  }
});

const UPDATE_USER = "user/update";
export const updateUser = createAsyncThunk(
  UPDATE_USER,
  async () => {
    const res = await apiClient.get('/auth/me');
    return normalizeUserPayload(res.data.payload);
  }
);

export type UserSliceState = {
  loading: boolean;
  error: SerializedError | null;
  data?: UserData | null;
  isLoggedIn: boolean;
  updated: boolean;
};

const initialState: UserSliceState = {
  loading: false,
  error: null,
  data: null,
  isLoggedIn: false,
  updated: false
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setEmailVerified(state, action) {
      if (state.data) {
        state.data.verifiedEmail = action.payload;
      }
    },
    setAdminVerified(state, action) {
      if (state.data) {
        state.data.verifiedByAdmin = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = normalizeUserPayload(
          action.payload as UserPayload,
          state.data?.type
        );
        state.isLoggedIn = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.data = null;
        state.isLoggedIn = false;
        state.error = action.error;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = normalizeUserPayload(
          action.payload as UserPayload,
          state.data?.type
        );
        state.isLoggedIn = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.data = null;
        state.isLoggedIn = false;
        state.error = action.error;
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.data = null;
        state.isLoggedIn = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.data = null;
        state.isLoggedIn = false;
        state.error = action.error;
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.updated = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const normalizedPayload = normalizeUserPayload(
          action.payload,
          state.data?.type
        );
        state.data = normalizedPayload
          ? { ...state.data, ...normalizedPayload }
          : null;
        state.isLoggedIn = true;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.data = null;
        state.isLoggedIn = false;
        state.error = action.error;
      });
  },
});

export const { setEmailVerified } = userSlice.actions;
export default userSlice.reducer;
